import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { TranslateModule } from '@ngx-translate/core';

import { ESelectedEntityType, type SelectedEntity } from '@common/types/layout.type';

import type { GridEntity } from '@sites/entity-core';

import { LayoutFacade } from '@web/store/layout/layout.facade';
import { GridsFacade } from '@web/store/page-structure/grids/grids.facade';
import { WidgetsFacade } from '@web/store/page-structure/widgets/widgets.facade';

import { NavigationGridWidgetItemComponent } from '../navigation-grid-widget-item';

import { NavigationDrawerService } from '../navigation-drawer.service';
import type { WebWidgetEntity } from '@web/types';

@Component({
    selector: 'web-navigation-grid-item',
    templateUrl: './navigation-grid-item.component.html',
    styleUrls: ['./navigation-grid-item.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [BsDropdownModule, TranslateModule, NavigationGridWidgetItemComponent],
})
export class NavigationGridItemComponent {
    public readonly entity = input.required<GridEntity>();
    public readonly index = input.required<number>();
    public readonly selectedEntity = input.required<SelectedEntity>();
    /** When false (e.g. a grid nested in a tab), clicking only toggles/activates and never selects the grid entity. */
    public readonly selectable = input(true);
    /** When false (e.g. a grid nested in a tab), the duplicate/delete actions are hidden. */
    public readonly showActions = input(true);

    public readonly gridSelected = output<string>();

    private readonly layoutFacade: LayoutFacade = inject(LayoutFacade);
    private readonly gridsFacade: GridsFacade = inject(GridsFacade);
    private readonly widgetsFacade: WidgetsFacade = inject(WidgetsFacade);
    private readonly navDrawerService: NavigationDrawerService = inject(NavigationDrawerService);

    public readonly showChildren = computed(() => this.navDrawerService.hasGridId(this.entity().id));

    public readonly isActive = computed(() => this.selectedEntity()?.entityId === this.entity().id);

    public readonly displayId = computed(() => 'grid_' + this.entity().id);

    private readonly widgetsEntities = toSignal(this.widgetsFacade.widgetsEntities$, { initialValue: {} });

    /** The grid's rows, each resolved from a list of widget ids to the widget entities themselves. */
    public readonly rows = computed(() => {
        const widgets = this.widgetsEntities();
        return this.entity().children.map((row) => (row as string[]).map((id) => widgets[id] as WebWidgetEntity));
    });

    public selectEntity(): void {
        this.gridSelected.emit(this.entity().id);

        if (this.navDrawerService.hasGridId(this.entity().id)) {
            this.navDrawerService.removeGridId(this.entity().id);
        } else {
            this.navDrawerService.addGridId(this.entity().id);
        }

        if (!this.selectable() || this.isActive()) {
            return;
        }

        this.layoutFacade.setSelectedEntityWithScroll({
            entityId: this.entity().id,
            entityType: ESelectedEntityType.grid,
        });
    }

    public async removeGrid(): Promise<void> {
        const grid = await firstValueFrom(this.gridsFacade.denormalizedById$(this.entity().id));

        if (this.isActive()) {
            this.layoutFacade.unsetEntity();
        }

        this.gridsFacade.remove(grid, this.index());
    }

    public async copyGrid(): Promise<void> {
        const grid = await firstValueFrom(this.gridsFacade.denormalizedById$(this.entity().id));

        this.gridsFacade.copy(grid, this.index());
    }

    /**
     * Re-emit selection upward (e.g. to a parent tab) when a nested grid widget is
     * selected, so the grid's owning container can react the same as a direct grid click.
     */
    public onChildWidgetSelected(): void {
        this.gridSelected.emit(this.entity().id);
    }
}
